import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpInterceptor,
    HttpErrorResponse,
    HttpHeaderResponse,
    HttpProgressEvent,
    HttpResponse,
    HttpUserEvent,
    HttpSentEvent
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { AuthenticationService } from '@/auth';
import { User } from '@/auth/models';
import { TsError } from './ts-error';

export const InterceptorSkipHeader = 'X-Skip-Interceptor';
@Injectable()
export class HttpJwtInterceptor implements HttpInterceptor {
    isRefreshingToken = false;
    refreshTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(private logger: NGXLogger, private authenticationService: AuthenticationService) {}

    // addToken is needed if we want to merge JwtInterceptor with ErrorInterceptor
    addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
        if (token) {
            const userId = this.authenticationService.currentUserValue
                ? this.authenticationService.currentUserValue.userHash
                : 'null';

            req = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`
                },
                withCredentials: true // To ensure that cookies get sent!
            });
        }
        return req;
    }

    isJson(obj: string): boolean {
        if (typeof obj === 'object' && obj !== null) {
            return true;
        }
        try {
            JSON.parse(obj);
        } catch (e) {
            return false;
        }
        return true;
    }

    getJson(obj: string): any {
        if (typeof obj === 'object' && obj !== null) {
            return obj;
        }
        try {
            const jsonObj = JSON.parse(obj);
            return jsonObj;
        } catch (e) {
            return null;
        }
    }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<
        | HttpSentEvent
        | HttpHeaderResponse
        | HttpProgressEvent
        | HttpResponse<any>
        | HttpUserEvent<any>
    > {
        if (req.headers.has(InterceptorSkipHeader)) {
            const headers = req.headers.delete(InterceptorSkipHeader);
            return next.handle(req.clone({ headers }));
        }
        this.logger.trace(
            'Intercepting HTTP URL',
            this.authenticationService.token,
            'Request:',
            req
        );
        return next.handle(this.addToken(req, this.authenticationService.token)).pipe(
            catchError((resp: any, _caught: Observable<any>) => {
                this.logger.warn('HTTP Error Encountered', req.url, resp);
                if (resp.status === 401) {
                    // Don't refresh token for login or refresh token. Verify url and throw an error in that case
                    if (req.url.includes('token/refresh') || req.url.includes('login')) {
                        // If refresh token failed, logout user and redirect to login page
                        if (req.url.includes('token/refresh')) {
                            this.authenticationService.logout();
                        }
                        return this.normalizeError(resp);
                    }
                    const user = this.authenticationService.currentUserValue;
                    if (user) {
                        return this.handleJWTTokenError(req, user, next);
                    }
                }
                return resp && resp.name === 'HttpErrorResponse'
                    ? this.handleHttpResponse(resp)
                    : this.normalizeError(resp);
            })
        );
    }

    normalizeError(response: any) {
        if (!response) {
            throwError(() => new TsError('Unknown Error'));
        }
        const error =
            'error' in response && this.isJson(response.error)
                ? this.getJson(response.error)
                : response;
        const message =
            error.message ||
            error.error ||
            ('server_response' in error ? error.server_response : null) ||
            ('server_response1' in error ? error.server_response1 : null) ||
            error.detail ||
            error.statusText;
        return throwError(() => new TsError(message));
    }

    handleHttpResponse(response: HttpErrorResponse) {
        if (response.status === 400 && response.error && response.error.error === 'invalid_grant') {
            // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
            return this.authenticationService.logout();
        }
        if (
            !this.isJson(response.error) &&
            response.message &&
            response.message.startsWith('Http failure response')
        ) {
            // The default error message includes the URL. So, we change it!
            // TODO: We can redirect to a custom page as well.
            const message = 'Http Status ' + response.status + ': ' + response.statusText;
            return throwError(() => new TsError(message));
        }
        return this.normalizeError(response);
    }

    handleJWTTokenError(req: HttpRequest<any>, user: User, next: HttpHandler) {
        if (!this.isRefreshingToken) {
            this.logger.debug('handleJWTTokenError => Refreshing Token');
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.refreshTokenSubject.next(null);

            return this.authenticationService.refreshToken(user).pipe(
                switchMap((newToken: any) => {
                    this.logger.debug('handleJWTTokenError => Refreshed Token');
                    if (newToken) {
                        this.isRefreshingToken = false;
                        this.refreshTokenSubject.next(newToken);
                        return next.handle(this.addToken(req, newToken));
                    }
                    // If we don't get a new token, we are in trouble so logout.
                    return this.authenticationService.logout();
                }),
                catchError(error => {
                    // If there is an exception calling 'refreshToken', bad news so logout.
                    this.logger.error('Error ==> ', error);
                    return this.authenticationService.logout();
                }),
                finalize(() => {
                    this.isRefreshingToken = false;
                })
            );
        } else {
            // wait until refreshTokenSubject has a non-null value before retrying the request
            return this.refreshTokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => {
                    return next.handle(this.addToken(req, token));
                })
            );
        }
    }
}
